import { createAsyncThunk } from '@reduxjs/toolkit';
import { MQTTSubscriber } from '../../mqtt/MQTTSubscriber';
import {
  completeProgress,
  updateStageProgress,
  updateSubQueryProgress
} from '../slices/legalQueryProgressSlice';
import { handleSimpleLegalQueryProgressUpdate } from './SimpleMQTTThunks'; // ✅ Import new simple handler
// DELETED - court-research feature removed
// import { startProgress as startCourtResearchProgress, ... } from '../slices/courtResearchProgressSlice';
// import { setCurrentCourtResearchResultId } from '../slices/uiSlice';
// import { DBCourtResearchResultThunks, courtResearchResultSelectByQuery, ... } from './DBServiceThunks';
import { DBMessageThunks } from './DBServiceThunks';

import {
  connectionFailure,
  connectionSuccess,
  disconnect,
  receiveMessage,
  startConnecting,
  updateSubscriptions
} from '../slices/mqttSlice';
import { RootState } from '../store';

// Thunk to connect to MQTT
export const connectMQTTThunk = createAsyncThunk(
  'mqtt/connect',
  async (payload: { userId: string; accessToken?: string }, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;

      // Check if already connected
      if (state.mqtt.isConnected) {
        console.log('[MQTT Thunk] Already connected to MQTT');
        return;
      }

      // Generate client ID
      const clientId = `legal-frontend_${payload.userId}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

      dispatch(startConnecting({ userId: payload.userId, clientId }));

      // Get MQTT subscriber instance
      const mqttSubscriber = MQTTSubscriber.getInstance();

      console.log(`🔌 [MQTT_DEBUG] Setting up MQTT handlers for userId: ${payload.userId}`);

      // ✅ Use new simplified legal query progress handler
      mqttSubscriber.onMessage('legal-query-progress', (progressData) => {
        console.log(`🔥 [MQTT_DEBUG] Raw MQTT legal-query-progress received:`, progressData);
        dispatch(handleSimpleLegalQueryProgressUpdate(progressData));
      });

      // DELETED - court-research feature removed
      // mqttSubscriber.onMessage('court-research-progress', (progressData) => { ... });

      // Connect to MQTT
      await mqttSubscriber.connect(payload.userId, payload.accessToken);

      // Check connection status with proper timeout
      setTimeout(() => {
        if (mqttSubscriber.isConnected()) {
          dispatch(connectionSuccess());
          dispatch(updateSubscriptions(mqttSubscriber.getCurrentSubscriptions()));
          console.log('[MQTT Thunk] ✅ Successfully connected to MQTT');
        } else {
          console.error('[MQTT Thunk] ❌ Failed to establish MQTT connection within timeout');
          dispatch(connectionFailure('Failed to establish MQTT connection - check broker availability and credentials'));
        }
      }, 5000); // Increased timeout for custom broker

    } catch (error: any) {
      console.error('[MQTT Thunk] Connection error:', error);
      dispatch(connectionFailure(error.message || 'Failed to connect to MQTT'));
    }
  }
);

// Thunk to disconnect from MQTT
export const disconnectMQTTThunk = createAsyncThunk(
  'mqtt/disconnect',
  async (_, { dispatch }) => {
    try {
      const mqttSubscriber = MQTTSubscriber.getInstance();
      mqttSubscriber.disconnect();
      dispatch(disconnect());
    } catch (error: any) {
      console.error('[MQTT Thunk] Disconnect error:', error);
    }
  }
);

// Thunk to send a message via MQTT
export const sendMQTTMessageThunk = createAsyncThunk(
  'mqtt/sendMessage',
  async (payload: { eventType: string; data: any }, { dispatch }) => {
    // Implementation would depend on specific requirements
    console.log('[MQTT Thunk] Sending message:', payload);
    dispatch(receiveMessage({ topic: payload.eventType, data: payload.data }));
  }
);

// Action to handle legal query progress updates from MQTT
export const handleLegalQueryProgressUpdate = createAsyncThunk(
  'mqtt/handleLegalQueryProgress',
  async (progressData: {
    queryId: string;
    stage: string;
    status: 'active' | 'completed' | 'error';
    progress?: number;
    details?: string;
    subQueryIndex?: number;
    subQueryTotal?: number;
  }, { dispatch, getState }) => {
    try {
      console.log(`📡 [PROGRESS_DEBUG] MQTT update received for queryId: ${progressData.queryId}`);
      console.log(`📡 [PROGRESS_DEBUG] Update details:`, progressData);

      // Check current Redux state
      const state = getState() as any;
      const activeProgresses = state.legalQueryProgress?.activeProgresses || {};
      const availableQueryIds = Object.keys(activeProgresses);

      console.log(`📡 [PROGRESS_DEBUG] Available queryIds in Redux: [${availableQueryIds.join(', ')}]`);
      console.log(`📡 [PROGRESS_DEBUG] Looking for queryId: ${progressData.queryId}`);
      console.log(`📡 [PROGRESS_DEBUG] QueryId exists in Redux: ${!!activeProgresses[progressData.queryId]}`);

      if (!activeProgresses[progressData.queryId]) {
        console.error(`🚨 [PROGRESS_DEBUG] QUERYID MISMATCH! MQTT wants "${progressData.queryId}" but Redux only has: [${availableQueryIds.join(', ')}]`);
      }

      const { queryId, stage, status, progress, details, subQueryIndex, subQueryTotal } = progressData;

      // Handle sub-query progress updates
      if (subQueryIndex !== undefined && subQueryTotal !== undefined) {
        // Determine if this sub-query is completed based on multiple signals
        const detailsLower = details?.toLowerCase() || '';
        const isSubQueryCompleted =
          detailsLower.includes('completed analysis of sub-query') ||
          detailsLower.includes('completed') ||
          detailsLower.includes('finished') ||
          detailsLower.includes('done') ||
          progress === 100;

        const subQueryStatus = isSubQueryCompleted ? 'completed' : 'active';

        console.log(`[MQTT Thunk] Sub-query ${subQueryIndex + 1}/${subQueryTotal}:`);
        console.log(`[MQTT Thunk]   Details: "${details}"`);
        console.log(`[MQTT Thunk]   Contains completion text: ${isSubQueryCompleted}`);
        console.log(`[MQTT Thunk]   Determined status: ${subQueryStatus}`);
        console.log(`[MQTT Thunk]   Backend progress: ${progress}`);

        dispatch(updateSubQueryProgress({
          queryId,
          subQueryIndex,
          subQueryTotal,
          subQueryDetails: details,
          progress,
          status: subQueryStatus
        }));

        // Update the main stage status and details, but let updateSubQueryProgress handle the progress calculation
        dispatch(updateStageProgress({
          queryId,
          stageId: stage,
          status: 'active',
          // DO NOT pass progress - let the incremental calculation in updateSubQueryProgress handle it
          details
        }));
        return;
      }

      // Handle stage transitions - let backend control the flow completely
      if (status === 'active') {
        console.log(`[MQTT Thunk] Setting stage ${stage} to active for query ${queryId}`);

        // For sub_query_processing stage, don't pass backend progress - let it start at 0%
        const shouldPassProgress = stage !== 'sub_query_processing';
        console.log(`[MQTT Thunk] Stage activation - shouldPassProgress: ${shouldPassProgress}, backend progress: ${progress}`);

        dispatch(updateStageProgress({
          queryId,
          stageId: stage,
          status: 'active',
          progress: shouldPassProgress ? progress : undefined,
          details
        }));
      } else if (status === 'completed') {
        console.log(`[MQTT Thunk] Setting stage ${stage} to completed for query ${queryId}`);
        dispatch(updateStageProgress({
          queryId,
          stageId: stage,
          status: 'completed',
          progress: 100,
          details
        }));

        // Only complete the entire process if this is the finalization stage
        if (stage === 'finalization') {
          console.log(`[MQTT Thunk] Completing entire process for query ${queryId}`);
          dispatch(completeProgress({
            queryId,
            success: true,
            details: details || 'Legal analysis completed successfully'
          }));

          // Refresh messages to get updated metadata (status: 'completed')
          setTimeout(async () => {
            try {
              const state = getState() as any;
              const currentChatId = state.ChatReducer.currentChatId;
              if (currentChatId) {
                console.log(`[MQTT Thunk] Refreshing messages after legal query completion for chat: ${currentChatId}`);
                await dispatch(DBMessageThunks.find({
                  chatId: currentChatId,
                  $paginate: false
                }));
              }
            } catch (error) {
              console.error('[MQTT Thunk] Error refreshing messages after completion:', error);
            }
          }, 1000); // Small delay to ensure backend has updated the message
        }
      } else if (status === 'error') {
        console.log(`[MQTT Thunk] Setting stage ${stage} to error for query ${queryId}`);
        // Complete the process with error
        dispatch(completeProgress({
          queryId,
          success: false,
          details: details || 'Error occurred during processing'
        }));

        // Refresh messages to get updated metadata (status: 'error')
        setTimeout(async () => {
          try {
            const state = getState() as any;
            const currentChatId = state.ChatReducer.currentChatId;
            if (currentChatId) {
              console.log(`[MQTT Thunk] Refreshing messages after legal query error for chat: ${currentChatId}`);
              await dispatch(DBMessageThunks.find({
                chatId: currentChatId,
                $paginate: false
              }));
            }
          } catch (error) {
            console.error('[MQTT Thunk] Error refreshing messages after error:', error);
          }
        }, 1000); // Small delay to ensure backend has updated the message
      }

    } catch (error) {
      console.error('[MQTT Thunk] Error processing legal query progress:', error);
      throw error;
    }
  }
);

// DELETED - court-research feature removed
// export const handleCourtResearchProgressUpdate = createAsyncThunk(...)

// Thunk to send a test MQTT request (for testing purposes)
export const sendMQTTTestRequestThunk = createAsyncThunk(
  'mqtt/sendTestRequest',
  async (payload: { command: string; params?: any }, { getState }) => {
    try {
      const state = getState() as RootState;

      if (!state.mqtt.isConnected || !state.mqtt.userId) {
        throw new Error('MQTT not connected or user ID not available');
      }

      // For now, just simulate a test request since sendRequest method doesn't exist
      const result = {
        success: true,
        data: {
          command: payload.command,
          params: payload.params,
          timestamp: new Date().toISOString()
        }
      };

      console.log('[MQTT Thunk] Test request result:', result);
      return result;
    } catch (error) {
      console.error('[MQTT Thunk] Error sending test request:', error);
      throw error;
    }
  }
);

// Check MQTT connection status
export const checkMQTTStatus = createAsyncThunk(
  'mqtt/checkStatus',
  async (_, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const mqttState = state.mqtt;

      const isConnected = MQTTSubscriber.getInstance().isConnected();
      const subscriptions = MQTTSubscriber.getInstance().getCurrentSubscriptions();

      console.log('[MQTT Thunks] MQTT status check:', {
        isConnected,
        subscriptions: subscriptions.length,
        stateConnected: mqttState.isConnected
      });

      // Update subscriptions if connected
      if (isConnected) {
        dispatch(updateSubscriptions(subscriptions));
        if (!mqttState.isConnected) {
          dispatch(connectionSuccess());
        }
      } else if (mqttState.isConnected) {
        // Connection lost
        dispatch(connectionFailure('Connection lost'));
      }

      return {
        isConnected,
        subscriptions,
        userId: mqttState.userId
      };
    } catch (error) {
      console.error('[MQTT Thunks] Error checking MQTT status:', error);
      return {
        isConnected: false,
        subscriptions: [],
        userId: null
      };
    }
  }
);

// Add message handler for specific event type
export const addMQTTMessageHandler = createAsyncThunk(
  'mqtt/addMessageHandler',
  async (
    { eventType, handler }: { eventType: string; handler: (data: any) => void },
    { dispatch }
  ) => {
    try {
      console.log('[MQTT Thunks] Adding message handler for:', eventType);

      // Wrap the handler to also dispatch to Redux
      const wrappedHandler = (data: any) => {
        console.log(`[MQTT Thunks] Received ${eventType} message:`, data);

        // Dispatch to Redux store for state tracking
        dispatch(receiveMessage({ topic: eventType, data }));

        // Call the original handler
        handler(data);
      };

      MQTTSubscriber.getInstance().onMessage(eventType, wrappedHandler);

      return { eventType };
    } catch (error) {
      console.error('[MQTT Thunks] Error adding message handler:', error);
      throw error;
    }
  }
);

// Test MQTT connection with detailed diagnostics
export const testMQTTConnection = createAsyncThunk(
  'mqtt/testConnection',
  async (_, { dispatch }) => {
    try {
      console.log('[MQTT Thunks] Testing MQTT connection...');

      const mqttSubscriber = MQTTSubscriber.getInstance();
      const testResult = await mqttSubscriber.testConnection();

      console.log('[MQTT Thunks] Connection test result:', testResult);

      if (!testResult.success) {
        dispatch(connectionFailure(`Connection test failed: ${testResult.message}`));
      } else {
        console.log('[MQTT Thunks] ✅ MQTT connection test passed');
      }

      return {
        ...testResult,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('[MQTT Thunks] Error testing MQTT connection:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown connection test error';
      dispatch(connectionFailure(errorMessage));

      return {
        success: false,
        message: errorMessage,
        broker: 'unknown',
        timestamp: Date.now()
      };
    }
  }
); 